import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalAndAlternates } from "@/lib/hreflang";
import { faqPageJson } from "@/lib/faq-schema";
import { faqQuestionNodes } from "@/lib/section-faqs";
import { WeightLossOptionsButtons } from "@/components/site/WeightLossOptionsButtons";
import { medicalWebPageScript } from "@/lib/medical-schema";
import { TelehealthCta } from "@/components/site/TelehealthCta";

const TITLE = "Semaglutide (Wegovy®) in Northern Virginia | Virginia Bariatric";
const DESCRIPTION =
  "Physician-supervised semaglutide (Wegovy®, Ozempic®) for weight loss in Herndon, VA. Prescribed by a board-certified bariatric surgeon. Injection & pill options.";
const URL = "https://virginiabariatric.com/medical-weight-loss/semaglutide";

const FAQS = [
  {
    q: "Do I need a prescription for semaglutide?",
    a: "Yes. Semaglutide is a prescription medication. At Virginia Bariatric Surgery, that starts with a medical evaluation—in person in Herndon or by telehealth—covering your health history, labs, prior weight-loss attempts, and goals. If semaglutide is appropriate, we prescribe it and see you regularly to adjust dosing.",
  },
  {
    q: "Is compounded semaglutide safe?",
    a: "We prescribe only FDA-approved semaglutide (Wegovy, Ozempic). Now that the FDA shortage designation has ended, mass-produced compounded versions are no longer permitted, and compounded products were never FDA-reviewed for safety, purity, or dosing accuracy. If a price seems too good to be true, ask what's actually in the vial.",
  },
  {
    q: "Will I regain weight if I stop taking semaglutide?",
    a: "Without a maintenance plan, most patients regain a significant portion of lost weight after stopping—in trial extensions, roughly two-thirds of it within a year. This is why we treat obesity as a chronic condition and build a long-term plan, whether that's ongoing medication, a structured taper, or a conversation about more durable options.",
  },
  {
    q: "Should I choose the Wegovy pill or the injection?",
    a: "Both produce comparable average weight loss in trials. The once-weekly injection has the longest track record; the once-daily pill (taken on an empty stomach) suits patients who prefer to avoid needles. Insurance coverage often differs between the two—we'll help you compare.",
  },
  {
    q: "Semaglutide or tirzepatide — which is better?",
    a: "In the only head-to-head trial (SURMOUNT-5), tirzepatide produced greater average weight loss (20.2% vs. 13.7%). But \"best\" depends on your insurance coverage, tolerability, and health profile—semaglutide has the strongest cardiovascular-protection evidence. We prescribe both and match the medication to the patient.",
  },
  {
    q: "Can I take semaglutide if I’ve had bariatric surgery?",
    a: "Often, yes. GLP-1 medications can be an effective tool for weight regain after a sleeve or bypass—and a bariatric surgeon is the ideal prescriber in that situation, because we can also evaluate whether anatomy is contributing.",
  },
];

export const Route = createFileRoute("/medical-weight-loss/semaglutide")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:url", content: URL },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "en_US" },
        { property: "og:locale:alternate", content: "es_LA" },
        {
        property: "og:image",
        content:
          "https://virginiabariatric.com/og/medications/og-semaglutide.png",
      },
        {
        property: "og:image:alt",
        content: "Semaglutide (Wegovy® & Ozempic®) for weight loss — Virginia Bariatric Surgery",
      },
        { property: "og:image:width", content: "2400" },
        { property: "og:image:height", content: "1260" },
        { name: "twitter:card", content: "summary_large_image" },
        {
        name: "twitter:image",
        content:
          "https://virginiabariatric.com/og/medications/og-semaglutide.png",
      },
        {
        name: "twitter:image:alt",
        content: "Semaglutide (Wegovy® & Ozempic®) for weight loss — Virginia Bariatric Surgery",
      },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
      ] : []),
    ],
    links: [...(import.meta.env.SSR ? canonicalAndAlternates("/medical-weight-loss/semaglutide") : []),
    ],
    scripts: import.meta.env.SSR ? [
      ...medicalWebPageScript("/medical-weight-loss/semaglutide"),
      {
        type: "application/ld+json",
        children: faqPageJson({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          // Every visible question on the page — the FAQ block plus the
          // question-style section headings — mapped to FAQPage.
          mainEntity: faqQuestionNodes("/medical-weight-loss/semaglutide", FAQS),
        }),
      },
    ] : [],
  }),
  component: SemaglutidePage,
});

function SemaglutidePage() {
  return (
    <article className="mx-auto max-w-3xl px-6 md:px-12 lg:px-32 xl:px-44 2xl:px-56 pt-4 pb-10 md:text-[1.125rem] leading-[1.6]">
      <header>
        <h1 className="text-[2.025rem] md:text-[2.7rem] font-serif">
          Semaglutide (Wegovy<sup>®</sup> &amp; Ozempic<sup>®</sup>) for Weight Loss in Northern
          Virginia
        </h1>
        <p className="mt-1 text-foreground/70 italic">
          Prescribed and managed by a board-certified bariatric surgeon
        </p>
      </header>

      <p className="mt-3 text-foreground/80">
        Semaglutide — best known by the brand names Wegovy<sup>®</sup> and Ozempic<sup>®</sup> — has
        transformed medical weight loss, helping patients lose 12&ndash;15% of their body weight in
        clinical trials. At Virginia Bariatric Surgery in Herndon, semaglutide is prescribed and
        managed by Dr. Matthew Fitzer, a fellowship-trained, board-certified bariatric surgeon — not
        a med spa or an online questionnaire.
      </p>
      <TelehealthCta />

      <p className="mt-3 text-foreground/80">
        Dr. Fitzer&rsquo;s experience using GLP-1s (and other medications) for weight loss began in
        2005.<sup>1</sup> Experience matters. The best results come from pairing the right
        medication with real medical oversight, nutrition support, and a plan for what happens next.
        We serve patients throughout Northern Virginia and the Washington, D.C. metro area —
        including Herndon, Reston, Fairfax, Loudoun, Arlington, Alexandria, Sterling, Ashburn,
        Chantilly, and Manassas — with both in-office
        and{" "}
        <Link to="/appointments" className="text-brand-deep underline hover:no-underline">
          telehealth visits
        </Link>
        .
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        What is semaglutide and how does it work?
      </h2>
      <p className="mt-3 text-foreground/80">
        Semaglutide is a <strong>GLP-1 receptor agonist</strong>. It mimics glucagon-like peptide-1,
        a hormone your gut releases when you eat. By activating GLP-1 receptors in the brain and
        digestive tract, semaglutide:
      </p>
      <ul className="mt-3 list-disc space-y-1 pl-5 text-foreground/80">
        <li>Reduces appetite and quiets &ldquo;food noise,&rdquo; the constant background urge to eat</li>
        <li>Slows stomach emptying, so you feel full sooner and stay full longer</li>
        <li>Improves the body&rsquo;s insulin response and blood-sugar control</li>
      </ul>
      <p className="mt-3 text-foreground/80">
        The result is that eating less stops feeling like a constant fight with your own biology.
        Most patients take semaglutide as a once-weekly injection<sup>2</sup> with a small
        pre-filled pen, gradually increasing the dose over several months to minimize side effects.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        Wegovy vs. Ozempic vs. the new Wegovy pill: what&rsquo;s the difference?
      </h2>
      <p className="mt-3 text-foreground/80">
        All three contain the same active ingredient (semaglutide), but they are approved for
        different uses:
      </p>
      <ul className="mt-3 list-disc space-y-2 pl-5 text-foreground/80">
        <li>
          <strong>Wegovy<sup>®</sup></strong> is FDA-approved specifically for chronic weight
          management (once-weekly injection, up to 2.4 mg).
        </li>
        <li>
          <strong>Ozempic<sup>®</sup></strong> is approved for type 2 diabetes; it is sometimes used
          off-label for weight loss, but insurance rarely covers it for that purpose.
        </li>
        <li>
          <strong>Oral Wegovy<sup>®</sup> (semaglutide 25 mg tablet)</strong> — approved by the FDA
          as the first GLP-1 pill for weight loss — is a once-daily option for patients who prefer
          to avoid injections, with weight loss in the OASIS 4 trial comparable to the injectable.
        </li>
      </ul>
      <p className="mt-3 text-foreground/80">
        Which version fits you depends on your health history, your insurance, and your preferences
        — exactly what we sort out at your first visit.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        How much weight will I lose on semaglutide?
      </h2>
      <p className="mt-3 text-foreground/80">
        In the landmark STEP 1 trial, adults taking weekly semaglutide 2.4 mg lost an{" "}
        <strong>average of about 15% of their body weight over 68 weeks</strong>, compared with
        roughly 2% on placebo. For a 250-pound patient, that&rsquo;s close to 40 pounds. About one
        in three patients in the trial lost 20% or more.
      </p>
      <p className="mt-3 text-foreground/80">
        Individual results vary — genetics, dose tolerance, and lifestyle all play a role. It&rsquo;s
        also worth knowing the honest comparisons: in the SURMOUNT-5 head-to-head trial,{" "}
        <Link to="/medical-weight-loss/tirzepatide" className="text-brand-deep underline hover:no-underline">
          tirzepatide (Zepbound<sup>®</sup>)
        </Link>{" "}
        produced greater average weight loss than semaglutide (20.2% vs. 13.7%), and{" "}
        <Link to="/services" className="text-brand-deep underline hover:no-underline">
          bariatric surgery
        </Link>{" "}
        still delivers the largest and most durable results — typically 60 to over 70% of excess
        weight. Dr. Fitzer has written a detailed comparison in{" "}
        <Link
          to="/dr-fitzer-dissects-the-news/$slug"
          params={{ slug: "semaglutide-versus-weight-loss-surgery-outcomes" }}
          className="text-brand-deep underline hover:no-underline"
        >
          Wegovy vs. Surgery Outcomes
        </Link>
        . Because our practice offers the full spectrum, our only incentive is to recommend what
        will actually work for you.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        Health benefits beyond the scale
      </h2>
      <p className="mt-3 text-foreground/80">
        Semaglutide is the most extensively studied weight-loss medication in history, and its
        benefits go well beyond weight:
      </p>
      <ul className="mt-3 list-disc space-y-2 pl-5 text-foreground/80">
        <li>
          <strong>Heart protection.</strong> In the SELECT trial, Wegovy reduced major
          cardiovascular events (heart attack, stroke, cardiovascular death) by about 20% in adults
          with obesity and existing heart disease — and it is FDA-approved for this use.
        </li>
        <li>
          <strong>Liver disease.</strong> Wegovy is FDA-approved to treat MASH (metabolic
          dysfunction&ndash;associated steatohepatitis, or &ldquo;fatty liver disease&rdquo;) with
          moderate to advanced fibrosis.
        </li>
        <li>
          <strong>Blood sugar.</strong> Semaglutide improves A1c and can prevent progression from
          prediabetes to{" "}
          <Link
            to="/resources/obesity-and-diabetes"
            preload="intent"
            className="text-brand-mid underline"
          >
            type 2 diabetes
          </Link>
          .
        </li>
        <li>
          Patients commonly report improvements in blood pressure, cholesterol, joint pain, sleep,
          and energy as weight comes down.
        </li>
      </ul>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        Who is a candidate for semaglutide?
      </h2>
      <p className="mt-3 text-foreground/80">You may qualify for semaglutide if you have:</p>
      <ul className="mt-3 list-disc space-y-1 pl-5 text-foreground/80">
        <li>A BMI of 30 or higher, or</li>
        <li>
          A BMI of 27 or higher with at least one weight-related condition such as high blood
          pressure, type 2 diabetes, sleep apnea, or high cholesterol
        </li>
        <li>A BMI under 27 in selected circumstances.</li>
      </ul>
      <p className="mt-3 text-foreground/80">
        Semaglutide is not appropriate for everyone. It should not be used during pregnancy, with a
        personal or family history of medullary thyroid carcinoma or MEN 2 syndrome, or with a
        history of pancreatitis without careful evaluation. Every new patient gets a full medical
        evaluation — not a checkbox form — before any prescription is written. Not sure where you
        stand? See{" "}
        <Link
          to="/medical-weight-loss/qualifications"
          preload="intent"
          className="text-brand-deep underline hover:no-underline"
        >
          Do I qualify for medication?
        </Link>{" "}
        or simply request an{" "}
        <Link to="/appointments" className="text-brand-deep underline hover:no-underline">
          appointment
        </Link>
        .
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        Side effects and safety: why supervision matters
      </h2>
      <p className="mt-3 text-foreground/80">
        Most side effects are digestive and improve as your body adjusts: nausea, constipation,
        diarrhea, and reflux are the most common, especially during dose increases. Occasionally,
        patients experience an odd tingling sensation or hypersensitivity of the skin. Less common
        but important risks include gallbladder problems, pancreatitis, and — the reason gradual
        dosing and follow-up matter — dehydration and rapid muscle loss when patients lose weight
        without adequate protein intake.
      </p>
      <p className="mt-3 text-foreground/80">
        Dr. Fitzer follows the emerging research closely, including questions raised about rare eye
        conditions in GLP-1 users — see his plain-English review,{" "}
        <Link
          to="/dr-fitzer-dissects-the-news/$slug"
          params={{ slug: "eye-problems-and-glp-1-medications" }}
          className="text-brand-deep underline hover:no-underline"
        >
          Eye Problems and GLP-1 Meds: What We Know
        </Link>
        . Our program pairs your prescription with dietitian-guided nutrition (protein targets,
        hydration) and regular follow-up so weight loss is safe, not just fast.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        Why get semaglutide from a bariatric <em>surgeon</em>?
      </h2>
      <p className="mt-3 text-foreground/80">
        Little-known fact: bariatric surgeons have more experience than anybody with managing
        weight-loss medications. Dr. Fitzer started prescribing the first GLP-1 medication
        (Byetta<sup>®</sup>) in 2005. Bariatric surgery practice often forces surgeons to think
        outside the box to help patients with stubborn, resistant physiology reach their weight-loss
        goals — Dr. Fitzer has been at it for over twenty-one years now. Plenty of places in
        Northern Virginia will hand or digitally send you a semaglutide prescription. Here&rsquo;s
        what&rsquo;s different when your prescriber is a fellowship-trained bariatric surgeon
        leading a{" "}
        <Link to="/our-program" className="text-brand-deep underline hover:no-underline">
          comprehensive weight-loss program
        </Link>
        :
      </p>
      <ul className="mt-3 list-disc space-y-2 pl-5 text-foreground/80">
        <li>
          <strong>A plan for weight regain.</strong> Weight regain after stopping GLP-1s is the
          field&rsquo;s biggest unsolved problem — Dr. Fitzer is{" "}
          <Link
            to="/dr-fitzer-dissects-the-news/$slug"
            params={{ slug: "weight-regain-after-wegovy-zepbound-treatment-part-3" }}
            className="text-brand-deep underline hover:no-underline"
          >
            three parts into his essay series on it
          </Link>
          . We&rsquo;re thinking about your maintenance strategy from day one.
        </li>
        <li>
          <strong>Real medical management.</strong> Dose adjustments, side-effect management, lab
          monitoring, and coordination with your other physicians. When necessary, Dr. Fitzer and
          his staff actually talk to your doctor — on the phone!
        </li>
        <li>
          <strong>Team support.</strong> Dietitian, mental-health, and exercise guidance included in
          our program — the ingredients that separate 8% results from 15%+ results.
        </li>
        <li>
          <strong>Options if medications don&rsquo;t deliver.</strong> If semaglutide doesn&rsquo;t
          get you to your goal — or can&rsquo;t maintain you there — we can escalate to{" "}
          <Link to="/medical-weight-loss/tirzepatide" className="text-brand-deep underline hover:no-underline">
            tirzepatide
          </Link>
          , combination approaches, or even discuss{" "}
          <Link to="/services" className="text-brand-deep underline hover:no-underline">
            surgical options
          </Link>
          . Med spas have a tool. We&rsquo;re experts with <em>all</em> of them.
        </li>
        <li>
          <strong>Getting preauthorized.</strong> We have lots of experience overcoming denials. Dr.
          Fitzer has personally written reams of patient-specific appeal letters to
          overturn—very often successfully—a medication denial.
        </li>
      </ul>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        Cost and insurance coverage in Virginia
      </h2>
      <p className="mt-3 text-foreground/80">
        Coverage for Wegovy varies widely by plan and changes frequently. Many commercial plans
        cover it with prior authorization (which our office handles); Virginia Medicaid and some
        employer plans have their own rules; and as of 2026 a Medicare pilot program covers GLP-1s
        for weight management for eligible beneficiaries. For patients paying out of pocket,
        manufacturer self-pay programs have brought monthly costs down substantially from list
        price. We also prescribe{" "}
        <Link
          to="/medical-weight-loss/oral-medications"
          preload="intent"
          className="text-brand-deep underline hover:no-underline"
        >
          affordable non-GLP-1 alternatives
        </Link>{" "}
        for patients whose insurance, budget, or medical history makes a GLP-1 less suitable. We
        walk every patient through their real options — start with our{" "}
        <Link
          to="/medical-weight-loss/insurance"
          preload="intent"
          className="text-brand-deep underline hover:no-underline"
        >
          medication insurance coverage guide
        </Link>
        .
      </p>


      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">Semaglutide FAQs</h2>
      <div className="mt-3 space-y-4">
        {FAQS.map((f) => (
          <div key={f.q}>
            <h3 className="font-serif text-brand-mid text-[1.125rem]">{f.q}</h3>
            <p className="mt-1 text-foreground/80">{f.a}</p>
          </div>
        ))}
      </div>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        Next steps: start semaglutide with a physician who does this every day
      </h2>
      <p className="mt-3 text-foreground/80">
        Whether you&rsquo;re starting your first medication or switching from another program, we
        make it simple: one visit, a clear plan, and support at every step.{" "}
        <Link to="/appointments" className="text-brand-deep underline hover:no-underline">
          Request an appointment
        </Link>{" "}
        or call{" "}
        <a
          href="tel:+17037099771"
          aria-label="Call Virginia Bariatric Surgery at 703-709-9771"
          className="text-brand-deep underline hover:no-underline"
        >
          (703) 709-9771
        </a>
        . Serving Herndon, Reston, Fairfax, Loudoun, Arlington, Alexandria, Sterling, Ashburn,
        Chantilly, Manassas, and the greater
        Washington, D.C. metro area — telehealth available throughout Virginia.
      </p>

      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Link
          to="/appointments"
          className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          Request a consultation
        </Link>
        <WeightLossOptionsButtons />
      </div>

      <div className="mt-8 text-sm text-foreground/70">
        <ol className="list-decimal space-y-1 pl-5">
          <li>The first GLP-1 came out in 2005. It just took a while to catch on.</li>
          <li>
            Sometimes the optimal way to help a semaglutide patient maintain a certain level of
            weight loss is by stretching the one-week interval to ten or fourteen days.
          </li>
        </ol>
      </div>
    </article>
  );
}
